import { ThemeOptions } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import { deprecatedColors } from './deprecatedColors';
import fonts, { NotoSans } from './fonts';
import { boxShadow } from './box-shadows';
import { colors } from './colors';

const mkTheme: ThemeOptions = {
    typography: {
        fontFamily: fonts.notoSans,
        allVariants: {},
        body1: {
            fontFamily: fonts.body,
            fontSize: '0.8125rem', // ~13px
        },
        body2: {
            fontFamily: fonts.body2,
            fontSize: '0.8125rem', // ~13px
        },
        h1: {
            fontFamily: fonts.header,
            fontSize: '1.25rem', // or 19.2 px?
            fontWeight: 500,
        },
        h2: {
            fontFamily: fonts.header,
            fontSize: '1rem',
            fontWeight: 400,
        },
        h3: {
            fontFamily: fonts.notoSans,
            fontSize: '16px',
            fontWeight: 400,
        },
        h4: {
            fontFamily: fonts.notoSans,
            fontSize: '12px',
            fontWeight: 300,
        },
        button: {
            fontFamily: fonts.button,
            fontSize: '0.8rem',
            textTransform: 'none',
        },
        myCustomersBody: {
            fontFamily: NotoSans.style.fontFamily,
            fontSize: '14px',
            fontWeight: 300,
        },
        productAttribute: { fontFamily: fonts.header },
        myCustomersPrice: {
            fontFamily: fonts.notoSans,
            fontSize: '14px',
            fontWeight: 400,
        },
    },
    palette: {
        mode: 'light',
        primary: { main: colors.corePink },
        secondary: { main: colors.pink2 },
    },
    components: {
        MuiChip: {
            styleOverrides: {
                root: {
                    fontSize: '12px',
                    paddingX: 0.5,
                    fontWeight: 300,
                    fontFamily: NotoSans.style.fontFamily,
                },
            },
        },
        MuiTab: { styleOverrides: { root: { opacity: 1 } } },
        MuiTextField: {
            variants: [
                {
                    props: { variant: 'outlined', size: 'small' },
                    style: {
                        borderRadius: '8px',
                    },
                },
            ],
        },
        MuiSelect: {
            variants: [
                {
                    props: { variant: 'outlined', size: 'small' },
                    style: {
                        borderRadius: '4px',
                    },
                },
            ],
        },
        MuiTypography: {
            variants: [
                {
                    props: { variant: 'underlined' },
                    style: {
                        display: 'block',
                        textAlign: 'left',
                        backgroundImage: `linear-gradient(${deprecatedColors.darkRed} 0 0)`,
                        backgroundPosition: 'bottom left',
                        backgroundSize: '98px 1px',
                        paddingBottom: '8px',
                        backgroundRepeat: 'no-repeat',
                        color: colors.pink2,
                        fontFamily: fonts.header,
                        fontSize: '1rem',
                        fontWeight: 400,
                    },
                },
                {
                    props: { variant: 'birthdayDate' },
                    style: {
                        fontFamily: fonts.header,
                        fontSize: '16px',
                        fontWeight: 400,
                        color: colors.pink2,
                    },
                },
            ],
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: '0.35rem',
                    boxShadow: 'unset',
                    border: 'solid 1px #EDEDED',
                },
            },
        },
        MuiCheckbox: {
            defaultProps: {
                color: 'secondary',
                checkedIcon: <CheckIcon
                    fontSize="small"
                    sx={{
                        border: `2.25px solid ${colors.pink2}`,
                        borderRadius: '2px',
                    }}
                />,
                icon: <CheckIcon
                    fontSize="small"
                    sx={{
                        border: '2.25px solid rgba(0,0,0,0.6)',
                        borderRadius: '2px',
                        color: 'transparent',
                    }}
                />,
            },
        },
        MuiCardContent: { styleOverrides: { root: { '&:last-child': { paddingBottom: 8 } } } },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    padding: '0.5rem 1rem',
                    backgroundColor: colors.gray1,
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#fff',
                    boxShadow: 'none',
                    '&:focus': { boxShadow: 'none' },
                },
            },
        },
        MuiIconButton: {
            variants: [
                {
                    props: { className: 'closeDialog' },
                    style: {
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
                        position: 'absolute',
                        zIndex: 10,
                        right: 8,
                        top: 8,
                        color: colors.black,
                        backgroundColor: colors.veryLightGray,
                    },
                },
            ],
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.white,
                    boxShadow: 'none',
                    border: '1px solid #EDEDED',
                },
            },
        },
        MuiButtonGroup: {
            styleOverrides: {
                root: {
                    button: { boxShadow: 'none' },
                },
            },
        },
        MuiButton: {
            variants: [
                {
                    props: { variant: 'viewMore' },
                    style: {
                        fontFamily: fonts.body,
                        width: `${150 / 16}rem`,
                        height: '2rem',
                        borderRadius: '1rem',
                        backgroundColor: colors.corePink,
                        color: colors.pink2,
                        '&:focus': { backgroundColor: colors.corePink },
                    },
                },
                {
                    props: { variant: 'seeAll' },
                    style: {
                        fontFamily: fonts.notoSans,
                        fontWeight: '700',
                        fontSize: '16px',
                        color: colors.pink2,
                        '&:hover': { color: colors.pink1 },
                    },
                },
                {
                    props: { variant: 'black' },
                    style: {
                        fontFamily: fonts.body,
                        borderRadius: '2px',
                        backgroundColor: colors.white,
                        color: colors.black,
                    },
                },
                {
                    props: { variant: 'order' },
                    style: {
                        fontFamily: fonts.body,
                        borderRadius: '4px',
                        backgroundColor: colors.black,
                        color: colors.white,
                        fontSize: '1rem',
                        '&:disabled': {
                            backgroundColor: colors.gray1,
                            color: colors.gray3,
                        },
                        '&:hover': {
                            backgroundColor: `${colors.black}BB`,
                            color: colors.white,
                        },
                    },
                },
                {
                    props: { variant: 'darkRed' },
                    style: {
                        fontFamily: fonts.body,
                        borderRadius: '0.25rem',
                        backgroundColor: colors.secondary1,
                        color: colors.pink2,
                        '&:hover': {
                            backgroundColor: colors.secondary1,
                            color: colors.pink2,
                        },
                    },
                },
                {
                    props: { variant: 'cancel' },
                    style: {
                        fontFamily: NotoSans.style.fontFamily,
                        borderRadius: '0.25rem',
                        backgroundColor: colors.veryLightGray,
                        color: colors.black,
                    },
                },
                {
                    props: { variant: 'cancelRed' },
                    style: {
                        fontFamily: fonts.body,
                        borderRadius: '0.25rem',
                        backgroundColor: colors.veryLightGray,
                        color: deprecatedColors.darkRed,
                    },
                },
                {
                    props: { variant: 'black', size: 'small' },
                    style: {
                        minWidth: 0,
                        padding: 0,
                        fontFamily: fonts.body,
                        borderRadius: '2px',
                        backgroundColor: colors.white,
                        color: colors.black,
                    },
                },
            ],
            styleOverrides: {
                root: {
                    color: colors.pink2,
                    boxShadow: 'none',
                    '&:focus': { boxShadow: 'none' },
                    '&:hover': { backgroundColor: colors.hover, boxShadow: 'none' },
                },
            },
        },
        // TODO: Target MuiBreadcrumbs with the styling so not all MuiLinks get affected
        MuiLink: {
            variants: [
                {
                    props: { color: deprecatedColors.gray2 },
                    style: {
                        color: deprecatedColors.gray2,
                        '&:hover': { color: colors.black },
                        textDecoration: 'none',
                    },
                },
            ],
        },
        MuiRadio: { styleOverrides: { root: { '&.Mui-checked': { color: colors.pink2 } } } },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: colors.black,
                    fontWeight: 300,
                    fontFamily: NotoSans.style.fontFamily,
                    fontSize: 14,
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    fontWeight: 300,
                    fontFamily: NotoSans.style.fontFamily,
                    fontSize: 14,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontWeight: 300,
                    fontFamily: NotoSans.style.fontFamily,
                    fontSize: 14,
                },
            },
        },
    },
};

export default mkTheme;
